import {Form, FormSubmitHandler} from 'platform/components';
import {VStack, Box} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useExtractDeliveryNoteDocumentMutation} from '@dms/api/core';
import {useGetHandlingUnitsQuery, useGetSuppliersQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {FileItem, ZipItem} from '@prompt-bridge/shared/validations';

import {ApiError, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useVatRatesOptions} from '../../../hooks/useVatRatesOptions';
import {getOptionsFromHandlingUnits} from '../../../utils/getOptionsFromHandlingUnits';
import {getOptionsFromSuppliers} from '../../../utils/getOptionsFromSuppliers';
import {handleApiError} from '../../../utils/handleApiError';
import {refreshDmsDatagrid} from '../../../utils/refreshDmsDatagrid';
import {
  DocumentExtractionImportFormDefinition,
  DocumentExtractionImportFormDefinitionSchema,
} from '../types/DocumentExtractionImportFormDefinition';
import {DocumentExtractionImportFooterActions} from './DocumentExtractionImportFooterActions';
import {DocumentExtractionImportFormFields} from './DocumentExtractionImportFormFields';
import {DocumentExtractionImportHeader} from './DocumentExtractionImportHeader';

interface DocumentExtractionImportFormProps extends RequiredTestIdProps {
  onClose: VoidFunction;
  onSuccess: VoidFunction;
  onError: VoidFunction;
}

export function DocumentExtractionImportForm(props: DocumentExtractionImportFormProps) {
  const [createImportTask] = useExtractDeliveryNoteDocumentMutation();

  const {data: suppliers} = useGetSuppliersQuery();
  const {data: units} = useGetHandlingUnitsQuery();
  const [vatOptions] = useVatRatesOptions();

  const handleSubmit: FormSubmitHandler<DocumentExtractionImportFormDefinition> = async (
    formValues,
    setErrors
  ) => {
    const validFilesWithFileId =
      formValues.filesToImport?.filter((file) => Boolean(file.storageServiceFileId)) || [];

    const validFilesWithZipIds =
      formValues.filesToImport?.filter((file) => file.zipIds && file.zipIds.length > 0) || [];

    const files = validFilesWithFileId.map((file) => ({
      fileId: file.storageServiceFileId,
      fileName: file.fileName,
    })) as FileItem[];

    const zips = validFilesWithZipIds.flatMap(
      (file) =>
        file.zipIds?.map((zipId) => ({
          zipId,
          fileName: file.fileName,
        })) as ZipItem[]
    );

    const filesToBeExtracted = files && files.length > 0 ? files : undefined;

    const zipsToBeExtracted = zips && zips.length > 0 ? zips : undefined;

    if (isNilOrEmpty(validFilesWithFileId) && isNilOrEmpty(validFilesWithZipIds)) {
      return setErrors([{name: 'filesToImport', message: i18n.t('errors.field_is_required')}]);
    }

    await createImportTask({
      files: filesToBeExtracted,
      zips: zipsToBeExtracted,
      suppliers: getOptionsFromSuppliers(suppliers),
      units: getOptionsFromHandlingUnits(units),
      vat: vatOptions,
    })
      .unwrap()
      .then(() => {
        props.onSuccess();
      })
      .then(() => refreshDmsDatagrid(EXTRACTED_DELIVERY_NOTES))
      .catch((error: ApiError) => {
        handleApiError(error);
        props.onError();
      });
  };

  const defaultValues: DocumentExtractionImportFormDefinition = {
    taskType: 'EXTRACT_DELIVERY_NOTE',
    filesToImport: null,
  };

  return (
    <Form<DocumentExtractionImportFormDefinition>
      experimentalZodSchema={DocumentExtractionImportFormDefinitionSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <Box padding={[0, 6, 6, 6]}>
            <VStack align="center" justify="center" spacing={4}>
              <DocumentExtractionImportHeader />
              <DocumentExtractionImportFormFields
                control={control}
                formApi={formApi}
                data-testid={suffixTestId('formFields', props)}
              />
            </VStack>
          </Box>
          <DocumentExtractionImportFooterActions
            control={control}
            onClose={props.onClose}
            data-testid={suffixTestId('footer', props)}
          />
        </VStack>
      )}
    </Form>
  );
}

const EXTRACTED_DELIVERY_NOTES = 'extracted-delivery-notes';
